"use client";

import { c as _c } from "react/compiler-runtime";
import { Image } from "@/components/Image";
import type { ButtonProps } from "@/components/ui/Button";
import { Button } from "@/components/ui/Button";
import { IconButton } from "@/components/ui/IconButton";
import { Spinner } from "@/components/ui/Spinner";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { FileIcon, LinkIcon, PlayIcon, PlusIcon, UploadIcon, VideoIcon, XIcon, ZoomInIcon } from "lucide-react";
import type { ChangeEvent, DragEvent, ReactNode, Ref } from "react";
import { useImperativeHandle, useRef, useState } from "react";
import cn from "twsx";

// The one upload surface. Every file chooser, drop target, picked-file row and
// stored-file thumbnail in the app renders through these parts, so call sites
// never build their own <input type="file">, dashed box or file chip.

export type UploadHandle = {
  open: () => void;
};
type UploadInputProps = {
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  capture?: "user" | "environment";
  onFiles: (files: File[]) => void;
};
function useFileInput(t0, handleRef) {
  const $ = _c(15);
  const {
    accept,
    multiple,
    disabled,
    capture,
    onFiles
  } = t0;
  const inputRef = useRef(null);
  let t1;
  if ($[0] !== disabled) {
    t1 = () => {
      if (disabled || !inputRef.current) {
        return;
      }
      inputRef.current.value = "";
      inputRef.current.click();
    };
    $[0] = disabled;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const open = t1;
  let t2;
  if ($[2] !== open) {
    t2 = () => ({
      open
    });
    $[2] = open;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useImperativeHandle(handleRef, t2);
  const t3 = accept && accept !== "*" ? accept : undefined;
  let t4;
  if ($[4] !== onFiles) {
    t4 = event_0 => {
      const files = Array.from(event_0.target.files ?? []);
      event_0.target.value = "";
      if (files.length > 0) {
        onFiles(files);
      }
    };
    $[4] = onFiles;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== capture || $[7] !== disabled || $[8] !== multiple || $[9] !== t3 || $[10] !== t4) {
    t5 = <input ref={inputRef} type="file" hidden={true} accept={t3} multiple={multiple} disabled={disabled} capture={capture} onClick={_temp} onChange={t4} />;
    $[6] = capture;
    $[7] = disabled;
    $[8] = multiple;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const input = t5;
  let t6;
  if ($[12] !== input || $[13] !== open) {
    t6 = {
      input,
      open
    };
    $[12] = input;
    $[13] = open;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}

/** A Button that opens the native file chooser. */
/** Use when a person uploads files, by button, dropzone or tile. */
function _temp(event) {
  return event.stopPropagation();
}
export function UploadButton(t0) {
  const $ = _c(25);
  let accept;
  let children;
  let disabled;
  let handleRef;
  let multiple;
  let onFiles;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      accept,
      multiple,
      disabled,
      onFiles,
      handleRef,
      children,
      variant: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = accept;
    $[2] = children;
    $[3] = disabled;
    $[4] = handleRef;
    $[5] = multiple;
    $[6] = onFiles;
    $[7] = props;
    $[8] = t1;
  } else {
    accept = $[1];
    children = $[2];
    disabled = $[3];
    handleRef = $[4];
    multiple = $[5];
    onFiles = $[6];
    props = $[7];
    t1 = $[8];
  }
  const variant = t1 === undefined ? "secondary" : t1;
  let t2;
  if ($[9] !== accept || $[10] !== disabled || $[11] !== multiple || $[12] !== onFiles) {
    t2 = {
      accept,
      multiple,
      disabled,
      onFiles
    };
    $[9] = accept;
    $[10] = disabled;
    $[11] = multiple;
    $[12] = onFiles;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const {
    input,
    open
  } = useFileInput(t2, handleRef);
  let t3;
  if ($[14] !== children) {
    t3 = children ?? <><UploadIcon />Upload</>;
    $[14] = children;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== disabled || $[17] !== open || $[18] !== props || $[19] !== t3 || $[20] !== variant) {
    t4 = <Button type="button" variant={variant} disabled={disabled} onClick={open} {...props}>{t3}</Button>;
    $[16] = disabled;
    $[17] = open;
    $[18] = props;
    $[19] = t3;
    $[20] = variant;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  let t5;
  if ($[22] !== input || $[23] !== t4) {
    t5 = <>{t4}{input}</>;
    $[22] = input;
    $[23] = t4;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  return t5;
}

/** A file chooser opened imperatively (`handleRef.current.open()`) from a menu item or custom trigger. */
export function UploadTrigger(t0) {
  const $ = _c(12);
  const {
    accept,
    multiple,
    disabled,
    capture,
    onFiles,
    handleRef,
    children
  } = t0;
  let t1;
  if ($[0] !== accept || $[1] !== capture || $[2] !== disabled || $[3] !== multiple || $[4] !== onFiles) {
    t1 = {
      accept,
      multiple,
      disabled,
      capture,
      onFiles
    };
    $[0] = accept;
    $[1] = capture;
    $[2] = disabled;
    $[3] = multiple;
    $[4] = onFiles;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const {
    input,
    open
  } = useFileInput(t1, handleRef);
  let t2;
  if ($[6] !== children || $[7] !== open) {
    t2 = children?.(open);
    $[6] = children;
    $[7] = open;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== input || $[10] !== t2) {
    t3 = <>{t2}{input}</>;
    $[9] = input;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
const dropzoneVariants = cva(["flex w-full grow flex-col items-center justify-center gap-2 px-6 py-8", "rounded-settings-list-view-item border border-dashed", "text-center text-base text-text-tertiary [&>svg]:size-5 [&>svg]:text-text-quaternary"], {
  variants: {
    active: {
      true: "border-accent bg-primary-50",
      false: "border-border-input bg-bg-surface"
    }
  },
  defaultVariants: {
    active: false
  }
});

/** A dashed drop target with a select button. */
export function UploadDropzone(t0) {
  const $ = _c(31);
  const {
    accept,
    multiple,
    disabled: t1,
    isLoading: t2,
    onFiles,
    fileName,
    prompt: t3,
    selectLabel: t4
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const isLoading = t2 === undefined ? false : t2;
  const prompt = t3 === undefined ? "Drag and drop a file to upload" : t3;
  const selectLabel = t4 === undefined ? "Select file" : t4;
  const [isDragging, setIsDragging] = useState(false);
  let t5;
  if ($[0] !== disabled) {
    t5 = event => {
      event.preventDefault();
      return !disabled;
    };
    $[0] = disabled;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const accepts = t5;
  let t6;
  if ($[2] !== isDragging) {
    t6 = dropzoneVariants({
      active: isDragging
    });
    $[2] = isDragging;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] !== accepts) {
    t7 = event_0 => setIsDragging(accepts(event_0));
    $[4] = accepts;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setIsDragging(false);
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== accepts || $[8] !== multiple || $[9] !== onFiles) {
    t9 = event_1 => {
      setIsDragging(false);
      const files = Array.from(event_1.dataTransfer.files).slice(0, multiple ? undefined : 1);
      if (accepts(event_1) && files.length > 0) {
        onFiles(files);
      }
    };
    $[7] = accepts;
    $[8] = multiple;
    $[9] = onFiles;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <FileIcon aria-hidden="true" />;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const t11 = fileName && "font-medium text-text-primary";
  let t12;
  if ($[12] !== t11) {
    t12 = cn(t11);
    $[12] = t11;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const t13 = fileName || prompt;
  let t14;
  if ($[14] !== t12 || $[15] !== t13) {
    t14 = <span className={t12}>{t13}</span>;
    $[14] = t12;
    $[15] = t13;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== accept || $[18] !== disabled || $[19] !== isLoading || $[20] !== multiple || $[21] !== onFiles || $[22] !== selectLabel) {
    t15 = <UploadButton accept={accept} multiple={multiple} disabled={disabled} isLoading={isLoading} onFiles={onFiles} size="sm">{selectLabel}</UploadButton>;
    $[17] = accept;
    $[18] = disabled;
    $[19] = isLoading;
    $[20] = multiple;
    $[21] = onFiles;
    $[22] = selectLabel;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== isLoading || $[25] !== t14 || $[26] !== t15 || $[27] !== t6 || $[28] !== t7 || $[29] !== t9) {
    t16 = <div className={t6} aria-busy={isLoading} onDragOver={t7} onDragLeave={t8} onDrop={t9}>{t10}{t14}{t15}</div>;
    $[24] = isLoading;
    $[25] = t14;
    $[26] = t15;
    $[27] = t6;
    $[28] = t7;
    $[29] = t9;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  return t16;
}
const fileKindIcons = {
  file: FileIcon,
  video: VideoIcon,
  link: LinkIcon
};

/** One picked or stored file: icon, name, caption, and open/remove actions. */
export function UploadFile(t0) {
  const $ = _c(22);
  const {
    name,
    caption,
    kind: t1,
    isUploading: t2,
    href,
    onOpen,
    onRemove,
    disabled
  } = t0;
  const kind = t1 === undefined ? "file" : t1;
  const isUploading = t2 === undefined ? false : t2;
  const Icon = fileKindIcons[kind];
  let t3;
  if ($[0] !== href || $[1] !== name || $[2] !== onOpen) {
    t3 = href ? <a href={href} target="_blank" rel="noopener noreferrer" className="truncate text-text-link hover:underline" aria-label={`${name} (opens in a new tab)`}>{name}</a> : onOpen ? <button type="button" onClick={onOpen} className="truncate text-left font-medium hover:underline focus-visible:outline-none">{name}</button> : <span className="truncate font-medium">{name}</span>;
    $[0] = href;
    $[1] = name;
    $[2] = onOpen;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const label = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cn("flex w-full min-w-0 items-center gap-2.5 px-2.5 py-2", "rounded-control border border-border-primary bg-bg-surface text-base text-text-primary");
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== Icon || $[6] !== isUploading) {
    t5 = <span aria-hidden="true" className="flex size-7 shrink-0 items-center justify-center rounded-editor-block bg-bg-primary text-text-tertiary [&>svg]:size-4">{isUploading ? <Spinner size="sm" /> : <Icon />}</span>;
    $[5] = Icon;
    $[6] = isUploading;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== caption || $[9] !== isUploading) {
    t6 = (isUploading || caption) && <span className="truncate text-sm text-text-tertiary">{isUploading ? "Uploading\u2026" : caption}</span>;
    $[8] = caption;
    $[9] = isUploading;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== label || $[12] !== t6) {
    t7 = <span className="flex min-w-0 flex-1 flex-col">{label}{t6}</span>;
    $[11] = label;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== disabled || $[15] !== isUploading || $[16] !== onRemove) {
    t8 = onRemove && !isUploading && <IconButton label="Remove file" type="button" size={24} disabled={disabled} onClick={onRemove}><XIcon /></IconButton>;
    $[14] = disabled;
    $[15] = isUploading;
    $[16] = onRemove;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t5 || $[19] !== t7 || $[20] !== t8) {
    t9 = <div className={t4}>{t5}{t7}{t8}</div>;
    $[18] = t5;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}
const tileVariants = cva(["relative flex shrink-0 cursor-pointer flex-col items-center justify-center gap-2 overflow-hidden p-0", "rounded-settings-list-view-item border border-border-popover bg-bg-surface text-text-tertiary outline-none", "hover:border-border-secondary", "focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-accent"], {
  variants: {
    size: {
      sm: "size-32",
      md: "size-48",
      lg: "size-64",
      fill: "h-48 w-full"
    }
  },
  defaultVariants: {
    size: "md"
  }
});
const tileImageSizes = {
  sm: "128px",
  md: "192px",
  lg: "256px",
  fill: "(max-width: 768px) 50vw, 25vw"
} as const;

/** A square thumbnail of a stored file (image, video poster or file icon) that opens it. */
export function UploadTile(t0) {
  const $ = _c(17);
  const {
    file,
    size: t1,
    onOpen
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  let t2;
  if ($[0] !== file.type) {
    t2 = file.type.startsWith("image/");
    $[0] = file.type;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isImage = t2;
  let t3;
  if ($[2] !== file.type) {
    t3 = file.type.startsWith("video/");
    $[2] = file.type;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const isVideo = t3;
  const t4 = `View ${file.name}`;
  let t5;
  if ($[4] !== size) {
    t5 = tileVariants({
      size
    });
    $[4] = size;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== file.name || $[7] !== file.url || $[8] !== isImage || $[9] !== isVideo || $[10] !== size) {
    t6 = isImage ? <Image src={file.url} alt={file.name} className="object-cover" fill={true} sizes={tileImageSizes[size ?? "md"]} unoptimized={true} /> : isVideo ? <><video src={file.url} className="absolute inset-0 size-full object-cover" preload="metadata" /><span aria-hidden="true" className="relative flex size-10 items-center justify-center rounded-full bg-scrim text-white [&>svg]:size-4"><PlayIcon /></span></> : <><FileIcon aria-hidden="true" className="size-6" /><span className="line-clamp-2 px-3 text-base">{file.name}</span></>;
    $[6] = file.name;
    $[7] = file.url;
    $[8] = isImage;
    $[9] = isVideo;
    $[10] = size;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== onOpen || $[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = <button type="button" aria-label={t4} className={t5} onClick={onOpen}>{t6}</button>;
    $[12] = onOpen;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
const slotVariants = cva("group relative shrink-0", {
  variants: {
    size: {
      md: "size-40",
      lg: "size-48"
    }
  },
  defaultVariants: {
    size: "md"
  }
});

/**
 * A square photo slot: an "Add photo" drop target when empty, or the photo
 * with open, remove and replace controls once filled.
 */
export function UploadSlot(t0) {
  const $ = _c(34);
  const {
    label,
    imageSrc,
    imageAlt,
    badge,
    emptyLabel: t1,
    replaceLabel: t2,
    size,
    accept: t3,
    disabled: t4,
    isUploading: t5,
    onFiles,
    onInvalidDrop,
    onOpen,
    onRemove,
    handleRef
  } = t0;
  const emptyLabel = t1 === undefined ? "Add photo" : t1;
  const replaceLabel = t2 === undefined ? "Change photo" : t2;
  const accept = t3 === undefined ? "image/*" : t3;
  const disabled = t4 === undefined ? false : t4;
  const isUploading = t5 === undefined ? false : t5;
  const blocked = disabled || isUploading;
  let t6;
  if ($[0] !== accept || $[1] !== blocked || $[2] !== onFiles) {
    t6 = {
      accept,
      disabled: blocked,
      onFiles
    };
    $[0] = accept;
    $[1] = blocked;
    $[2] = onFiles;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const {
    input,
    open
  } = useFileInput(t6, handleRef);
  let t7;
  if ($[4] !== size) {
    t7 = slotVariants({
      size
    });
    $[4] = size;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== blocked) {
    t8 = event => {
      event.preventDefault();
      event.dataTransfer.dropEffect = !blocked && event.dataTransfer.types.includes("Files") ? "copy" : "none";
    };
    $[6] = blocked;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== blocked || $[9] !== onFiles || $[10] !== onInvalidDrop) {
    t9 = event_0 => {
      event_0.preventDefault();
      if (blocked) {
        return;
      }
      const [file, ...rest] = Array.from(event_0.dataTransfer.files);
      if (!file || rest.length > 0) {
        onInvalidDrop?.();
        return;
      }
      onFiles([file]);
    };
    $[8] = blocked;
    $[9] = onFiles;
    $[10] = onInvalidDrop;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== badge) {
    t10 = badge && <span className="absolute left-2 top-2 z-10">{badge}</span>;
    $[12] = badge;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== blocked || $[15] !== disabled || $[16] !== emptyLabel || $[17] !== imageAlt || $[18] !== imageSrc || $[19] !== isUploading || $[20] !== label || $[21] !== onOpen || $[22] !== onRemove || $[23] !== open || $[24] !== replaceLabel) {
    t11 = imageSrc ? <div className="relative size-full overflow-hidden rounded-settings-list-view-item border border-border-popover"><button type="button" aria-label={label} className={cn("absolute inset-0 cursor-pointer outline-none", "focus-visible:outline-1 focus-visible:-outline-offset-2 focus-visible:outline-accent")} onClick={onOpen}><Image src={imageSrc} alt={imageAlt} className="object-cover" fill={true} sizes="192px" /></button>{!disabled && <><span className="absolute right-2 top-2 flex items-center gap-2">{onOpen && <IconButton label="View photo" variant="secondary" type="button" reveal={true} onClick={onOpen}><ZoomInIcon /></IconButton>}{onRemove && <IconButton label="Remove photo" variant="secondary" type="button" reveal={true} onClick={onRemove}><XIcon /></IconButton>}</span><span className="absolute inset-x-2 bottom-2 flex"><Button type="button" variant="secondary" className="w-full" isLoading={isUploading} disabled={blocked} onClick={open}>{replaceLabel}</Button></span></>}</div> : <button type="button" aria-label={label} disabled={blocked} onClick={open} className={cn("flex size-full cursor-pointer flex-col items-center justify-center gap-1.5", "rounded-settings-list-view-item border border-dashed border-border-input bg-bg-primary", "text-base font-medium text-text-secondary outline-none", "hover:border-border-input-hover hover:bg-fill-hover-subtle", "focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-accent", "disabled:cursor-not-allowed disabled:opacity-disabled [&>svg]:size-5 [&>svg]:text-text-tertiary")}>{isUploading ? <Spinner size="lg" /> : <PlusIcon aria-hidden="true" />}<span>{isUploading ? "Uploading\u2026" : emptyLabel}</span></button>;
    $[14] = blocked;
    $[15] = disabled;
    $[16] = emptyLabel;
    $[17] = imageAlt;
    $[18] = imageSrc;
    $[19] = isUploading;
    $[20] = label;
    $[21] = onOpen;
    $[22] = onRemove;
    $[23] = open;
    $[24] = replaceLabel;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== input || $[27] !== label || $[28] !== t10 || $[29] !== t11 || $[30] !== t7 || $[31] !== t8 || $[32] !== t9) {
    t12 = <div role="group" aria-label={label} className={t7} onDragOver={t8} onDrop={t9}>{t10}{t11}{input}</div>;
    $[26] = input;
    $[27] = label;
    $[28] = t10;
    $[29] = t11;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
}